import { ReactElement, useState } from 'react';
import {
  Datagrid,
  FilterForm,
  FunctionField,
  List,
  Pagination,
  SelectInput,
  TextInput,
  TopToolbar,
  useNotify,
  useTranslate,
} from 'react-admin';
import { Box, Button, Card, Chip, Dialog, Stack, Tooltip, Typography } from '@mui/material';

import { EmptyContent } from 'src/components/empty-content';
import { ProviderItem } from 'src/pages/admin/services/xbitRelayService';
import { ProviderAuditLockState } from 'src/pages/admin/services/xbitRelayProviderAuditService';

import { LockStateDialog } from './LockStateDialog';
import { PROVIDER_AUDIT_LOCKS_RESOURCE, PROVIDER_AUDIT_ROWS_PER_PAGE } from './constants';
import { formatUnixMs, truncateMiddle } from './helpers';

const LocksListActions = ({ providers }: { providers: ProviderItem[] }) => (
  <TopToolbar
    sx={{
      minHeight: 90,
      alignItems: 'center',
      px: 3,
      pt: 1,
      overflow: 'visible',
      width: '100%',
    }}
  >
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        width: '100%',
        flexWrap: 'wrap',
        '& form': {
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          flexWrap: 'wrap',
          width: '100%',
        },
        '& .ra-input': { mt: 0, mb: 0 },
        '& .MuiFormControl-root': { mt: 0, mb: 0 },
        '& .MuiInputBase-root': { minHeight: 40 },
      }}
    >
      <FilterForm
        filters={[
          <TextInput
            key="userId"
            source="userId"
            label="module.xbitRelayProviderAudit.userId"
            alwaysOn
            resettable
            size="small"
            sx={{ minWidth: 240 }}
          />,
          <SelectInput
            key="provider"
            source="provider"
            label="module.xbitRelayProviderAudit.provider"
            choices={providers.map((item) => ({ id: item.provider, name: item.provider }))}
            alwaysOn
            resettable
            emptyText="actions.all"
            size="small"
            sx={{ minWidth: 220 }}
          />,
        ]}
      />
    </Box>
  </TopToolbar>
);

export const LocksTab = ({ providers }: { providers: ProviderItem[] }) => {
  const t = useTranslate();
  const notify = useNotify();
  const [popup, setPopup] = useState<ReactElement>();

  const copy = (value: string) => {
    if (!value) return;
    navigator.clipboard.writeText(value);
    notify('module.xbitRelayProviderAudit.copied', { type: 'info' });
  };

  const closePopup = () => setPopup(undefined);

  return (
    <>
      <Card sx={{ pb: 2, mt: 2 }}>
        {/*
          Its own resource key and store: `/locks` is a different dataset from the Mismatches tab,
          and syncing a second list to the same URL query would clobber the other tab's params.
        */}
        <List
          resource={PROVIDER_AUDIT_LOCKS_RESOURCE}
          storeKey={false}
          disableSyncWithLocation
          title={false}
          actions={<LocksListActions providers={providers} />}
          empty={false}
          perPage={20}
          exporter={false}
          pagination={<Pagination rowsPerPageOptions={PROVIDER_AUDIT_ROWS_PER_PAGE} />}
        >
          <Box sx={{ overflow: 'auto' }}>
            <Datagrid
              rowClick={false}
              bulkActionButtons={false}
              empty={<EmptyContent />}
              sx={{
                '& .RaDatagrid-headerCell': { fontWeight: 600 },
                '& .RaDatagrid-rowCell': { py: 1.25 },
              }}
            >
              <FunctionField
                label="module.xbitRelayProviderAudit.userId"
                sortable={false}
                render={(record: ProviderAuditLockState) => (
                  <Tooltip title={record.userId || ''} arrow>
                    <Typography
                      variant="body2"
                      sx={{ fontFamily: 'monospace', cursor: 'pointer', whiteSpace: 'nowrap' }}
                      onClick={() => copy(record.userId)}
                    >
                      {truncateMiddle(record.userId)}
                    </Typography>
                  </Tooltip>
                )}
              />
              <FunctionField
                label="module.xbitRelayProviderAudit.provider"
                sortable={false}
                render={(record: ProviderAuditLockState) => (
                  <Typography variant="body2">{record.provider || '-'}</Typography>
                )}
              />
              <FunctionField
                label="module.xbitRelayProviderAudit.lockStatus"
                sortable={false}
                render={(record: ProviderAuditLockState) => (
                  <Chip
                    size="small"
                    label={
                      record.locked
                        ? t('module.xbitRelayProviderAudit.locked')
                        : t('module.xbitRelayProviderAudit.unlocked')
                    }
                    color={record.locked ? 'error' : 'success'}
                    variant={record.locked ? 'filled' : 'outlined'}
                  />
                )}
              />
              <FunctionField
                label="module.xbitRelayProviderAudit.lockedAt"
                sortable={false}
                render={(record: ProviderAuditLockState) => (
                  <Typography
                    variant="body2"
                    sx={{ whiteSpace: 'nowrap', fontFamily: 'monospace' }}
                  >
                    {formatUnixMs(record.lockedAtUnixMs)}
                  </Typography>
                )}
              />
              <FunctionField
                label="module.xbitRelayProviderAudit.lockReason"
                sortable={false}
                render={(record: ProviderAuditLockState) => (
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                    {record.reason || '-'}
                  </Typography>
                )}
              />
              <FunctionField
                label="common.actions"
                sortable={false}
                render={(record: ProviderAuditLockState) =>
                  // Nothing to act on once the pair is unlocked.
                  record.locked ? (
                    <Stack direction="row" justifyContent="flex-end">
                      <Button
                        variant="outlined"
                        size="small"
                        color="warning"
                        onClick={() =>
                          setPopup(
                            <LockStateDialog
                              userId={record.userId}
                              provider={record.provider}
                              initialState={record}
                              onClose={closePopup}
                            />
                          )
                        }
                      >
                        {t('module.xbitRelayProviderAudit.unlock')}
                      </Button>
                    </Stack>
                  ) : null
                }
              />
            </Datagrid>
          </Box>
        </List>
      </Card>

      <Dialog open={Boolean(popup)} onClose={closePopup} maxWidth="sm" fullWidth>
        {popup}
      </Dialog>
    </>
  );
};
